import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { WebsiteLayout } from '@/components/website/WebsiteLayout';
import { Calendar, ArrowRight, Search, Clock, TrendingUp, BookOpen, Filter } from 'lucide-react';
import api from '@/utils/api';

const CATEGORY_COLORS = {
  'Investment Tips': { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/20' },
  'Property Guide': { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/20' },
  'Project Update': { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/20' },
  'Market Update': { bg: 'bg-purple-500/10', text: 'text-purple-600', border: 'border-purple-500/20' },
  'Legal': { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/20' },
  'News': { bg: 'bg-cyan-500/10', text: 'text-cyan-600', border: 'border-cyan-500/20' },
  'General': { bg: 'bg-slate-500/10', text: 'text-slate-600', border: 'border-slate-500/20' },
};

const getCatStyle = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS['General'];

const readTime = (content) => {
  const words = (content || '').replace(/<[^>]*>/g, '').split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};

const FeaturedCard = ({ blog }) => {
  const style = getCatStyle(blog.category);
  return (
    <Link to={`/blog/${blog.slug}`} className="group block" data-testid="featured-blog">
      <div className="relative bg-gradient-to-br from-[#0A0A0F] via-[#142038] to-[#0A0A0F] rounded-3xl overflow-hidden shadow-2xl border border-[#1A2332]">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNDOUE4NEMiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDE4YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2em0wIDM2YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2eiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        <div className="relative p-6 sm:p-10 lg:p-14">
          <div className="flex items-center gap-3 mb-5">
            <span className="px-3 py-1 bg-[#DAA520]/15 text-[#DAA520] text-xs font-bold uppercase tracking-wider rounded-full border border-[#DAA520]/20">
              Featured
            </span>
            <span className={`px-3 py-1 ${style.bg} ${style.text} text-xs font-bold uppercase tracking-wider rounded-full border ${style.border}`}>
              {blog.category}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4 group-hover:text-[#DAA520] transition-colors duration-300">
            {blog.title}
          </h2>
          <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-6 max-w-3xl line-clamp-3">{blog.excerpt}</p>
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <span className="flex items-center gap-1.5 text-slate-500 text-xs sm:text-sm">
              <Calendar size={14} className="text-[#DAA520]" />
              {new Date(blog.created_at).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-1.5 text-slate-500 text-xs sm:text-sm">
              <Clock size={14} className="text-[#DAA520]" />
              {readTime(blog.content)} min read
            </span>
            <span className="ml-auto inline-flex items-center gap-2 text-[#DAA520] font-bold text-sm group-hover:gap-3 transition-all">
              Read Article <ArrowRight size={16} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const BlogCard = ({ blog, index }) => {
  const style = getCatStyle(blog.category);
  return (
    <Link to={`/blog/${blog.slug}`} className="group block" data-testid={`blog-card-${index}`}>
      <article className="h-full bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-1.5 border border-slate-100/80">
        {/* Gradient Header */}
        <div className="h-2 bg-gradient-to-r from-[#DAA520] via-[#FFD700] to-[#DAA520]" />
        <div className="p-5 sm:p-6 flex flex-col h-[calc(100%-8px)]">
          <div className="flex items-center gap-2 mb-3">
            <span className={`px-2.5 py-1 ${style.bg} ${style.text} text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded-full border ${style.border}`}>
              {blog.category}
            </span>
            <span className="text-slate-400 text-[10px] sm:text-xs flex items-center gap-1">
              <Clock size={10} /> {readTime(blog.content)} min
            </span>
          </div>
          <h3 className="font-bold text-base sm:text-lg text-[#0A0A0F] group-hover:text-[#DAA520] transition-colors duration-300 mb-2.5 line-clamp-2 leading-snug">
            {blog.title}
          </h3>
          <p className="text-slate-500 text-xs sm:text-sm leading-relaxed line-clamp-2 mb-4 flex-1">{blog.excerpt}</p>
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <span className="text-slate-400 text-[10px] sm:text-xs flex items-center gap-1">
              <Calendar size={11} />
              {new Date(blog.created_at).toLocaleDateString('hi-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            <span className="text-[#DAA520] font-semibold text-xs sm:text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
              Read <ArrowRight size={13} />
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get('/public/blogs').then(r => setBlogs(r.data || [])).catch(() => {});
    api.get('/public/website-settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const cats = ['All'];
    const catSet = new Set();
    blogs.forEach(b => { if (b.category && !catSet.has(b.category)) { catSet.add(b.category); cats.push(b.category); } });
    return cats;
  }, [blogs]);

  const filtered = useMemo(() => {
    let result = blogs;
    if (activeCategory !== 'All') result = result.filter(b => b.category === activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(b => b.title.toLowerCase().includes(q) || (b.excerpt || '').toLowerCase().includes(q) || (b.keywords || '').toLowerCase().includes(q));
    }
    return result;
  }, [blogs, activeCategory, searchQuery]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <WebsiteLayout>
      <Helmet>
        <title>Blog | Property Investment Tips & Real Estate Guide | Ganesa Homestate</title>
        <meta name="description" content="Ganesa Homestate Blog - Property investment tips, Khatushyam Ji real estate guide, legal tips, market updates. Free consultation available." />
        <meta name="keywords" content="ganesa homestate blog, property investment tips, khatushyam ji property, real estate guide rajasthan" />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-[#0A0A0F] py-10 sm:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNDOUE4NEMiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDE4YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2em0wIDM2YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-0.5 bg-[#DAA520]" />
                <span className="text-[#DAA520] text-xs sm:text-sm font-semibold uppercase tracking-[0.2em]">Blog</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Property <span className="text-[#DAA520]">Insights</span>
              </h1>
              <p className="text-slate-400 text-sm sm:text-base mt-2 max-w-lg">Investment tips, market updates aur property guides - Ganesa Homestate experts se</p>
            </div>
            <div className="flex items-center gap-2 bg-[#1A2332] rounded-xl px-3 py-2.5 border border-[#1A2332] focus-within:border-[#DAA520]/40 transition-colors w-full sm:w-72">
              <Search size={16} className="text-slate-500 shrink-0" />
              <input
                type="text"
                data-testid="blog-search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="bg-transparent text-white text-sm w-full outline-none placeholder:text-slate-500"
              />
            </div>
          </div>

          {/* Category Pills */}
          <div className="flex gap-2 mt-6 overflow-x-auto pb-2 scrollbar-hide" data-testid="blog-categories">
            {categories.map(cat => {
              const count = cat === 'All' ? blogs.length : blogs.filter(b => b.category === cat).length;
              return (
                <button key={cat} onClick={() => setActiveCategory(cat)}
                  className={`shrink-0 px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 flex items-center gap-1.5
                    ${activeCategory === cat
                      ? 'bg-[#DAA520] text-[#0A0A0F] shadow-lg shadow-[#DAA520]/20'
                      : 'bg-[#1A2332] text-slate-400 hover:text-white hover:bg-[#1A2332]/80 border border-[#1A2332]'
                    }`}>
                  {cat}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeCategory === cat ? 'bg-[#0A0A0F]/20 text-[#0A0A0F]' : 'bg-[#0A0A0F] text-slate-500'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-8 sm:py-14 bg-[#F8F9FB]" data-testid="blog-listing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {filtered.length > 0 ? (
            <>
              {/* Featured Post */}
              {featured && <div className="mb-8 sm:mb-12"><FeaturedCard blog={featured} /></div>}

              {/* CTA Banner */}
              <div className="mb-8 sm:mb-12 bg-gradient-to-r from-[#DAA520] via-[#FFD700] to-[#DAA520] rounded-2xl p-5 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="blog-cta-banner">
                <div>
                  <h3 className="text-[#0A0A0F] font-bold text-lg sm:text-xl">Free Property Consultation</h3>
                  <p className="text-[#0A0A0F]/70 text-xs sm:text-sm mt-1">Khatushyam Ji me plot investment ke baare me jaanein - bilkul FREE!</p>
                </div>
                <Link to="/contact" className="shrink-0 px-6 py-3 bg-[#0A0A0F] text-[#DAA520] font-bold text-sm rounded-xl hover:bg-[#142038] transition-colors shadow-lg">
                  Enquiry Karein
                </Link>
              </div>

              {/* Blog Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {rest.map((b, i) => <BlogCard key={b.id} blog={b} index={i} />)}
              </div>

              {/* Bottom Stats */}
              <div className="mt-10 sm:mt-14 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm text-sm text-slate-500 border border-slate-100">
                  <BookOpen size={14} className="text-[#DAA520]" />
                  <span>Total <strong className="text-[#0A0A0F]">{blogs.length}</strong> articles published</span>
                </div>
              </div>

              {/* Bottom CTA */}
              <div className="mt-8 sm:mt-12 bg-[#0A0A0F] rounded-3xl p-6 sm:p-10 text-center border border-[#1A2332]" data-testid="blog-bottom-cta">
                <TrendingUp size={32} className="mx-auto text-[#DAA520] mb-4" />
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Property Me Invest Karna Chahte Hain?</h3>
                <p className="text-slate-400 text-sm max-w-lg mx-auto mb-6">Shri Shyama Township, Khatushyam Ji me premium plots available. 1000+ families ne trust kiya hai.</p>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <Link to="/contact" className="px-8 py-3.5 bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-[#0A0A0F] font-bold rounded-xl shadow-lg shadow-[#DAA520]/20 hover:shadow-xl transition-all text-sm">
                    Free Consultation Book Karein
                  </Link>
                  <a href={`https://wa.me/${(settings?.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                    className="px-8 py-3.5 border-2 border-[#25D366] text-[#25D366] font-bold rounded-xl hover:bg-[#25D366]/10 transition-all text-sm flex items-center justify-center gap-2">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                    WhatsApp Karein
                  </a>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-16 sm:py-24">
              <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-5">
                <Search size={32} className="text-slate-300" />
              </div>
              <h3 className="text-xl font-bold text-[#0A0A0F] mb-2">
                {searchQuery ? 'Koi article nahi mila' : 'Articles coming soon'}
              </h3>
              <p className="text-slate-500 text-sm">
                {searchQuery ? 'Doosre keywords se try karein' : 'Hum aapke liye insightful articles prepare kar rahe hain!'}
              </p>
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
                  className="mt-4 px-6 py-2.5 bg-[#0A0A0F] text-[#DAA520] rounded-xl text-sm font-medium">
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </WebsiteLayout>
  );
}
